import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationPicker from './LocationPicker';
import { findMatchingJobs, preloadMLModel } from '../services/jobMatchingService';
import '../styles/jobMatchingModal.css';

const JobMatchingModal = ({ isOpen, onClose, jobs }) => {
  const navigate = useNavigate();
  const locationPickerRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mlStatus, setMlStatus] = useState('idle'); // idle, loading, ready
  const [formData, setFormData] = useState({
    skill: '', // Single skill selection from dropdown
    experience: '',
    skillLevel: 'laborer', // Changed from verificationLevel to match schema
    location: null,
    radius: 50,
    jobTypes: [],
    workplace: 'On-site',
    hours: 'Full-time',
    education: '',
    desiredBenefits: [],
    availability: 'flexible',
  });

  // Blue-collar trade options
  const BLUE_COLLAR_SKILLS = [
    'Carpentry',
    'Electrical',
    'Plumbing',
    'Welding',
    'General Labor',
    'All Around',
    'Mason'
  ];

  const steps = [
    {
      title: 'Select Your Trade',
      description: 'What is your primary blue-collar trade?',
      field: 'skill'
    },
    {
      title: 'Your experience level',
      description: 'How would you rate your professional experience?',
      field: 'experience'
    },
    {
      title: 'Skill level',
      description: 'Are you a laborer, skilled worker, or specialist?',
      field: 'skillLevel'
    },
    {
      title: 'Your location',
      description: 'Pin your location on the map to find nearby jobs',
      field: 'location'
    },
    {
      title: 'Search radius',
      description: 'How far are you willing to travel for work?',
      field: 'radius'
    },
    {
      title: 'Job types (Optional)',
      description: 'What types of work interest you? (Optional - skip to see all)',
      field: 'jobTypes'
    },
    {
      title: 'Availability (Optional)',
      description: 'What is your work availability? (Optional - skip to see all)',
      field: 'availability'
    }
  ];

  // Preload ML model when modal opens for faster matching
  useEffect(() => {
    if (isOpen && mlStatus === 'idle') {
      setMlStatus('loading');
      preloadMLModel().then(() => {
        setMlStatus('ready');
      }).catch((err) => {
        console.warn('ML preload error:', err);
        setMlStatus('ready'); // Continue anyway
      });
    }
  }, [isOpen, mlStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (checked) {
      setFormData(prev => ({
        ...prev,
        jobTypes: [...prev.jobTypes, name]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        jobTypes: prev.jobTypes.filter(type => type !== name)
      }));
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
  };

  const handleNext = () => {
    const currentField = steps[currentStep].field;
    
    if (currentField === 'skill' && !formData.skill.trim()) {
      setError('Please select a trade');
      return;
    }
    if (currentField === 'experience' && !formData.experience) {
      setError('Please select your experience level');
      return;
    }
    if (currentField === 'location' && !formData.location) {
      setError('Please select a location on the map');
      return;
    }
    
    setError(null);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setError(null);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFindJobs = async () => {
    if (!formData.skill.trim()) {
      setError('Please select a trade');
      return;
    }
    if (!formData.experience) {
      setError('Please select your experience level');
      return;
    }
    if (!formData.location) {
      setError('Please select a location');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('%c[JobMatchingModal] Finding jobs with preferences:', 'background: #2196F3; color: white; font-weight: bold;', formData);
      console.log(`[JobMatchingModal] Total jobs passed to matching: ${jobs?.length || 0}`);
      
      if (jobs && jobs.length > 0) {
        console.log('[JobMatchingModal] Sample jobs:');
        jobs.slice(0, 3).forEach((job, idx) => {
          console.log(`  ${idx + 1}. "${job.title}" | Primary Skill: ${job.primary_skill} | Location: ${job.location}`);
        });
      }
      
      const matchingJobs = await findMatchingJobs(
        formData,
        jobs
      );

      console.log(`%c[JobMatchingModal] Matching complete. Results: ${matchingJobs.length} jobs`, 'background: #4CAF50; color: white;');

      // Store the matching results and preferences
      localStorage.setItem('matchingPreferences', JSON.stringify(formData));
      localStorage.setItem('matchingJobs', JSON.stringify(matchingJobs));

      onClose();
      navigate('/job-search-results', { state: { matchingJobs, preferences: formData } });
    } catch (err) {
      setError('Error finding jobs: ' + err.message);
      console.error('Job matching error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="job-matching-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} disabled={loading}>×</button>

        <div className="modal-header">
          <h2>Find Your Perfect Job Match</h2>
          <p className="step-counter">Step {currentStep + 1} of {steps.length}</p>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="modal-content">
          <div className="step-content">
            <h3>{currentStepData.title}</h3>
            <p className="step-description">{currentStepData.description}</p>

            {/* Skills Dropdown */}
            {currentStep === 0 && (
              <div className="form-group">
                <select
                  name="skill"
                  value={formData.skill}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">-- Select your trade --</option>
                  {BLUE_COLLAR_SKILLS.map((skill) => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Experience Level */}
            {currentStep === 1 && (
              <div className="form-group">
                <label>
                  <input
                    type="radio"
                    name="experience"
                    value="entry"
                    checked={formData.experience === 'entry'}
                    onChange={handleInputChange}
                  />
                  <span>Entry Level (0-2 years)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="experience"
                    value="intermediate"
                    checked={formData.experience === 'intermediate'}
                    onChange={handleInputChange}
                  />
                  <span>Intermediate (2-5 years)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="experience"
                    value="expert"
                    checked={formData.experience === 'expert'}
                    onChange={handleInputChange}
                  />
                  <span>Expert (5+ years)</span>
                </label>
              </div>
            )}

            {/* Skill Level */}
            {currentStep === 2 && (
              <div className="form-group">
                <label>
                  <input
                    type="radio"
                    name="skillLevel"
                    value="laborer"
                    checked={formData.skillLevel === 'laborer'}
                    onChange={handleInputChange}
                  />
                  <span>Laborer (No specific skills required)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="skillLevel"
                    value="skilled"
                    checked={formData.skillLevel === 'skilled'}
                    onChange={handleInputChange}
                  />
                  <span>Skilled Worker (Verified expertise)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="skillLevel"
                    value="specialist"
                    checked={formData.skillLevel === 'specialist'}
                    onChange={handleInputChange}
                  />
                  <span>Specialist (Advanced certifications)</span>
                </label>
              </div>
            )}

            {/* Location */}
            {currentStep === 3 && (
              <div className="form-group">
                <LocationPicker
                  ref={locationPickerRef}
                  onLocationSelect={handleLocationSelect}
                  selectedLocation={formData.location}
                />
              </div>
            )}

            {/* Search Radius */}
            {currentStep === 4 && (
              <div className="form-group">
                <div className="radius-selector">
                  <label>
                    <input
                      type="radio"
                      name="radius"
                      value={10}
                      checked={formData.radius === 10}
                      onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
                    />
                    <span>10 km</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="radius"
                      value={25}
                      checked={formData.radius === 25}
                      onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
                    />
                    <span>25 km</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="radius"
                      value={50}
                      checked={formData.radius === 50}
                      onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
                    />
                    <span>50 km</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="radius"
                      value={100}
                      checked={formData.radius === 100}
                      onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
                    />
                    <span>100 km</span>
                  </label>
                </div>
              </div>
            )}

            {/* Job Types */}
            {currentStep === 5 && (
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="fulltime"
                    checked={formData.jobTypes.includes('fulltime')}
                    onChange={handleCheckboxChange}
                  />
                  <span>Full-time</span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="parttime"
                    checked={formData.jobTypes.includes('parttime')}
                    onChange={handleCheckboxChange}
                  />
                  <span>Part-time</span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="freelance"
                    checked={formData.jobTypes.includes('freelance')}
                    onChange={handleCheckboxChange}
                  />
                  <span>Freelance</span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="contract"
                    checked={formData.jobTypes.includes('contract')}
                    onChange={handleCheckboxChange}
                  />
                  <span>Contract</span>
                </label>
              </div>
            )}

            {/* Availability */}
            {currentStep === 6 && (
              <div className="form-group">
                <label>
                  <input
                    type="radio"
                    name="availability"
                    value="flexible"
                    checked={formData.availability === 'flexible'}
                    onChange={handleInputChange}
                  />
                  <span>Flexible (Open to all)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="availability"
                    value="fulltime"
                    checked={formData.availability === 'fulltime'}
                    onChange={handleInputChange}
                  />
                  <span>Full-time only</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="availability"
                    value="parttime"
                    checked={formData.availability === 'parttime'}
                    onChange={handleInputChange}
                  />
                  <span>Part-time only</span>
                </label>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={handlePrevious}
            disabled={currentStep === 0 || loading}
          >
            Previous
          </button>

          {currentStep === steps.length - 1 ? (
            <button
              className="btn-primary"
              onClick={handleFindJobs}
              disabled={loading}
            >
              {loading ? 'Finding Jobs...' : 'Find My Jobs'}
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobMatchingModal;
